import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import './AdminAccount.css';

const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadLocations(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, []);

  const loadLocations = async (uid) => {
    try {
      setLoading(true);
      const db = getFirestore();
      const snapshot = await getDocs(collection(db, 'users', uid, 'locations'));
      const locs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLocations(locs);
    } catch (error) {
      console.error('Error loading locations:', error);
      Swal.fire('Error', 'Failed to load locations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const normalizeCountryCode = (country) => {
    if (!country) return 'US';
    const c = country.trim();
    if (c.length === 2 && /^[A-Za-z]{2}$/.test(c)) return c.toUpperCase();
    const map = {
      'united states': 'US',
      'united states of america': 'US',
      'usa': 'US',
      'canada': 'CA',
      'mexico': 'MX',
      'united kingdom': 'GB',
      'great britain': 'GB',
      'france': 'FR',
      'germany': 'DE',
      'spain': 'ES',
      'italy': 'IT',
      'australia': 'AU',
      'brazil': 'BR',
      'india': 'IN',
      'japan': 'JP',
      'china': 'CN',
      'south korea': 'KR',
      'netherlands': 'NL',
      'belgium': 'BE',
      'switzerland': 'CH',
      'austria': 'AT',
      'sweden': 'SE',
      'norway': 'NO',
      'denmark': 'DK',
      'finland': 'FI',
      'ireland': 'IE',
      'portugal': 'PT',
      'greece': 'GR',
      'poland': 'PL',
      'czech republic': 'CZ',
      'hungary': 'HU',
      'romania': 'RO',
      'bulgaria': 'BG',
      'croatia': 'HR',
      'slovenia': 'SI',
      'slovakia': 'SK',
      'lithuania': 'LT',
      'latvia': 'LV',
      'estonia': 'EE',
      'luxembourg': 'LU',
      'malta': 'MT',
      'cyprus': 'CY',
    };
    const lower = c.toLowerCase();
    if (map[lower]) return map[lower];
    return c;
  };

  const handleCreateStripeLocation = async (location) => {
    if (!user) return;
    try {
      Swal.fire({
        title: 'Creating Stripe Location...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });
      const functions = getFunctions();
      const createStripeTerminalLocationFn = httpsCallable(functions, 'createStripeTerminalLocation');
      await createStripeTerminalLocationFn({
        locationId: location.id,
        locationName: location.companyName || location.locationName || '',
        address: location.address || '',
        city: location.city || '',
        state: location.state || '',
        zip: location.zip || '',
        country: normalizeCountryCode(location.country),
        logoUrl: location.logoUrl || null,
      });
      await loadLocations(user.uid);
      Swal.fire('Success', 'Stripe Terminal location created successfully!', 'success');
    } catch (error) {
      console.error('Error creating Stripe Terminal location:', error);
      Swal.fire('Error', error.message || 'Failed to create Stripe Terminal location', 'error');
    }
  };

  const handleEditLocation = async (location) => {
    // The link that matters is stripeLocationId. stripeConfigurationId (the
    // tipping configuration) may be absent on locations linked before that
    // feature existed — the backend derives/creates the configuration from
    // the Stripe location itself (updateTerminalTips reads the location's
    // configuration_overrides; updateStripeTerminalLocationLogo creates one
    // when missing), so it must NOT gate navigation (it was wrongly
    // blocking already-linked locations with a "Not Linked" dialog).
    if (location.stripeLocationId) {
      navigate(`/admin/locations/edit/${location.id}`);
      return;
    }

    // Not linked — ask user if they want to create Stripe Terminal location first
    const result = await Swal.fire({
      title: 'Stripe Terminal Not Linked',
      text: `This location is not linked to a Stripe Terminal. You must link it before editing terminal settings. Create the link now?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Link Now',
      cancelButtonText: 'Skip & Edit',
    });

    if (result.isConfirmed) {
      // Create Stripe Terminal location
      try {
        Swal.fire({
          title: 'Linking to Stripe...',
          allowOutsideClick: false,
          didOpen: () => { Swal.showLoading(); }
        });
        const functions = getFunctions();
        const createStripeTerminalLocationFn = httpsCallable(functions, 'createStripeTerminalLocation');
        await createStripeTerminalLocationFn({
          locationId: location.id,
          locationName: location.companyName || location.locationName || '',
          address: location.address || '',
          city: location.city || '',
          state: location.state || '',
          zip: location.zip || '',
          country: normalizeCountryCode(location.country),
          logoUrl: location.logoUrl || null,
        });
        await loadLocations(user.uid);
        Swal.fire({
          icon: 'success',
          title: 'Linked!',
          text: 'Stripe Terminal location created. Redirecting to edit page...',
          timer: 1500,
          showConfirmButton: false,
        });
        navigate(`/admin/locations/edit/${location.id}`);
      } catch (error) {
        console.error('Error creating Stripe Terminal location:', error);
        Swal.fire('Error', error.message || 'Failed to create Stripe Terminal location', 'error');
      }
    } else {
      // User chose to skip, navigate anyway
      navigate(`/admin/locations/edit/${location.id}`);
    }
  };

  const handleDelete = async (locationId, companyName, stripeLocationId) => {
    const result = await Swal.fire({
      title: 'Delete Location?',
      text: `Are you sure you want to delete "${companyName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed && user) {
      try {
        if (stripeLocationId) {
          try {
            const functions = getFunctions();
            const deleteStripeTerminalLocationFn = httpsCallable(functions, 'deleteStripeTerminalLocation');
            await deleteStripeTerminalLocationFn({ stripeLocationId });
          } catch (stripeError) {
            console.error('Error deleting Stripe Terminal location:', stripeError);
            const proceed = await Swal.fire({
              title: 'Stripe Warning',
              text: 'Could not delete the Stripe Terminal location. Delete the local location anyway?',
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#dc3545',
              cancelButtonColor: '#6c757d',
              confirmButtonText: 'Delete Anyway',
              cancelButtonText: 'Cancel',
            });
            if (!proceed.isConfirmed) return;
          }
        }
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'locations', locationId));
        await loadLocations(user.uid);
        Swal.fire('Deleted', 'Location deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting location:', error);
        Swal.fire('Error', 'Failed to delete location', 'error');
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Locations">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Locations">
      <div className="admin-account-page">
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="m-0">Manage your business locations</h5>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/admin/locations/new')}
              >
                <i className="fas fa-plus mr-2"></i>Add Location
              </button>
            </div>
          </div>
        </div>

        {locations.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-store-slash fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">No locations yet</h4>
            <p className="text-muted">Click "Add Location" to add your first location</p>
          </div>
        ) : (
          <div className="row">
            {locations.map((location) => (
              <div key={location.id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-start mb-3">
                      {location.logoUrl ? (
                        <img
                          src={location.logoUrl}
                          alt={location.companyName || location.locationName}
                          className="mr-3"
                          style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div 
                          className="d-flex align-items-center justify-content-center mr-3" 
                          style={{ 
                            width: 48, 
                            height: 48, 
                            borderRadius: 12, 
                            background: 'rgba(255,107,53,0.1)',
                            color: '#ff6b35'
                          }}
                        >
                          <i className="fas fa-store fa-lg"></i>
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <h5 className="card-title font-weight-bold mb-1" style={{ color: '#ff6b35', fontSize: '1.1rem' }}>
                          {location.companyName || location.locationName || 'Unnamed Location'}
                        </h5>
                        {location.address && (
                          <p className="card-text text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                            <i className="fas fa-map-marker-alt mr-1"></i>
                            {location.address}
                          </p>
                        )}
                        <p className="card-text text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                          {location.city && `${location.city}, `}
                          {location.state && `${location.state} `}
                          {location.zip}
                        </p>
                      </div>
                    </div>
                    
                    {location.phone && (
                      <p className="mb-1" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-phone mr-2 text-muted"></i>
                        {location.phone}
                      </p>
                    )}
                    
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3" style={{ borderTop: '1px solid #eee' }}>
                      <div>
                        {location.acceptCash !== false && (
                          <span className="badge badge-success mr-1">Cash</span>
                        )}
                        {location.taxRate > 0 && (
                          <span className="badge badge-info mr-1">Tax {location.taxRate}%</span>
                        )}
                        {!location.stripeLocationId && (
                          <button
                            className="badge badge-warning border-0"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleCreateStripeLocation(location)}
                          >
                            <i className="fas fa-plug mr-1"></i>Not Validated
                          </button>
                        )}
                        {location.stripeLocationId && (
                          <span className="badge badge-secondary">
                            <i className="fas fa-check mr-1"></i>Validated
                          </span>
                        )}
                      </div>
                      <div>
                        <button 
                          className="btn btn-sm btn-outline-primary mr-2"
                          onClick={() => handleEditLocation(location)}
                        >
                          <i className="fas fa-edit mr-1"></i>Edit
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(location.id, location.companyName, location.stripeLocationId)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default LocationsPage;